import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { Truck, Check, Mail, Upload, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const deliveries = [
  { month: 'Jan 2028', schools: 287, booksPerSchool: 20, total: 5740, status: 'dispatched', tracking: 'SP-TRK-20280108-***', date: '8 Jan 2028' },
  { month: 'Feb 2028', schools: 287, booksPerSchool: 20, total: 5740, status: 'dispatched', tracking: 'SP-TRK-20280205-***', date: '5 Feb 2028' },
  { month: 'Mar 2028', schools: 287, booksPerSchool: 20, total: 5740, status: 'scheduled', tracking: '—', date: '5 Mar 2028' },
  { month: 'Apr 2028', schools: 287, booksPerSchool: 20, total: 5740, status: 'scheduled', tracking: '—', date: '7 Apr 2028' },
  { month: 'May 2028', schools: 287, booksPerSchool: 11, total: 3157, status: 'scheduled', tracking: '—', date: '5 May 2028' },
];

const schoolDeliveries = [
  { school: 'Raffles Girls\' School', month: 'Jan 2028', books: 20, tracking: 'SP-TRK-20280108-001', dispatched: '8 Jan', delivered: '10 Jan', status: 'delivered', ack: 'acknowledged' },
  { school: 'ACS (Independent)', month: 'Jan 2028', books: 20, tracking: 'SP-TRK-20280108-002', dispatched: '8 Jan', delivered: '11 Jan', status: 'delivered', ack: 'acknowledged' },
  { school: 'Nanyang Primary', month: 'Jan 2028', books: 20, tracking: 'SP-TRK-20280108-045', dispatched: '8 Jan', delivered: '12 Jan', status: 'delivered', ack: 'pending' },
  { school: 'Tampines Primary', month: 'Jan 2028', books: 20, tracking: 'SP-TRK-20280108-087', dispatched: '8 Jan', delivered: '—', status: 'in_transit', ack: 'pending' },
];

export default function CivicaDeliveryStatus() {
  const handleBulkDispatch = () => {
    toast.success('Batch marked as dispatched');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <Truck className="w-7 h-7 text-[#2E5F8A]" />
          Delivery Status Management
        </h1>
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <p className="text-sm text-blue-800">
            📦 Physical processing (RFID tagging, barcode labelling, spine labels, QC) is performed offline at Civica's processing centre. 
            This screen covers: dispatch updates, delivery tracking, and monitoring school acknowledgements.
          </p>
        </CardContent>
      </Card>

      <Tabs defaultValue="dispatch">
        <TabsList>
          <TabsTrigger value="dispatch">Update Dispatch Status</TabsTrigger>
          <TabsTrigger value="tracking">Live Delivery Tracking</TabsTrigger>
          <TabsTrigger value="acknowledgement">School Acknowledgement Monitor</TabsTrigger>
          <TabsTrigger value="discrepancy">Discrepancy Log</TabsTrigger>
        </TabsList>

        <TabsContent value="dispatch" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">CDS Cycle 1 2028 — Monthly Batch Updates</CardTitle>
              <CardDescription>Update dispatch status when books leave the processing centre</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Schools</TableHead>
                    <TableHead>Books/School</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Dispatch Status</TableHead>
                    <TableHead>Tracking</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deliveries.map((d, idx) => (
                    <TableRow key={idx} className="table-row-striped">
                      <TableCell className="font-medium">{d.month}</TableCell>
                      <TableCell>{d.schools}</TableCell>
                      <TableCell>{d.booksPerSchool}</TableCell>
                      <TableCell>{d.total.toLocaleString()}</TableCell>
                      <TableCell>
                        {d.status === 'dispatched' ? (
                          <Badge className="bg-emerald-50 text-emerald-700">✓ DISPATCHED {d.date}</Badge>
                        ) : (
                          <Badge className="bg-slate-50 text-slate-700">📅 Scheduled {d.date}</Badge>
                        )}
                      </TableCell>
                      <TableCell>{d.tracking}</TableCell>
                      <TableCell>
                        {d.status === 'dispatched' ? (
                          <Button size="sm" variant="outline">View</Button>
                        ) : (
                          <Button size="sm" className="bg-[#2E5F8A]" onClick={handleBulkDispatch}>
                            Mark Dispatched
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-4 flex gap-2">
                <Button variant="outline" data-testid="bulk-dispatch">
                  <Check className="w-4 h-4 mr-2" />
                  Bulk Mark Batch as Dispatched
                </Button>
                <Button variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  Bulk Upload Tracking Numbers via CSV
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tracking" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Per-school Delivery Table</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>School</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead>Books</TableHead>
                    <TableHead>Tracking No.</TableHead>
                    <TableHead>Dispatched</TableHead>
                    <TableHead>Delivered</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Acknowledgement</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schoolDeliveries.map((d, idx) => (
                    <TableRow key={idx} className="table-row-striped">
                      <TableCell className="font-medium">{d.school}</TableCell>
                      <TableCell>{d.month}</TableCell>
                      <TableCell>{d.books}</TableCell>
                      <TableCell className="text-xs">{d.tracking}</TableCell>
                      <TableCell>{d.dispatched}</TableCell>
                      <TableCell>{d.delivered}</TableCell>
                      <TableCell>
                        {d.status === 'delivered' && <Badge className="bg-emerald-50 text-emerald-700">✓ Delivered</Badge>}
                        {d.status === 'in_transit' && <Badge className="bg-blue-50 text-blue-700">🚚 In Transit</Badge>}
                      </TableCell>
                      <TableCell>
                        {d.ack === 'acknowledged' ? (
                          <Badge className="bg-emerald-50 text-emerald-700">✓ Acknowledged</Badge>
                        ) : (
                          <Badge className="bg-amber-50 text-amber-700">⏳ Pending</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-xs text-slate-500 mt-4">
                Support: slfcollections@civica.com.sg | 6225 8800 | Mon–Fri 8am–6.30pm
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="acknowledgement" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading">Jan 2028 Batch Acknowledgements</CardTitle>
                <CardDescription>Delivered: 248 | Acknowledged: 231 | Overdue: 17 ⚠️</CardDescription>
              </div>
              <Button variant="outline" className="text-amber-600">
                <Mail className="w-4 h-4 mr-2" />
                Send Reminder to 17 Schools
              </Button>
            </CardHeader>
          </Card>
        </TabsContent>

        <TabsContent value="discrepancy" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Discrepancy Log</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="p-4 border rounded-lg">
                  <div className="flex justify-between">
                    <div>
                      <p className="font-medium">RGS — Feb 2028 Batch 2</p>
                      <p className="text-sm text-slate-500">1 book torn cover ("Code Red") | Reported 12 Feb 2028</p>
                      <p className="text-sm text-emerald-600">Resolution: Replacement dispatched 14 Feb</p>
                    </div>
                    <Badge className="bg-emerald-50 text-emerald-700 h-fit">✓ Resolved</Badge>
                  </div>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="flex justify-between">
                    <div>
                      <p className="font-medium">ACS (I) — Jan 2028 Batch 1</p>
                      <p className="text-sm text-slate-500">2 books not matching PO | Reported 19 Jan 2028</p>
                    </div>
                    <Badge className="bg-amber-50 text-amber-700 h-fit">⏳ Open</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
